/**
 * SailCloth.ts — Three.js wrapper around the pure `clothSolver` grid.
 *
 * Owns a `THREE.Mesh` whose indexed `BufferGeometry` mirrors the solver grid
 * one-to-one. Each frame the wrapper accumulates wall-clock `dt` into a fixed
 * timestep (1/60 high, 1/30 low), steps the pure solver, then writes positions
 * and recomputes vertex normals — the same `needsUpdate` + `computeVertexNormals`
 * pattern the old static sail already used.
 *
 * Wind model (frozen in the OpenSpec design, refined in the research doc):
 *
 *   worldWind = (cos θ, 0, −sin θ) · windStrength          (0..1.5)
 *   w_local   = inverse(meshWorldQuaternion) · worldWind
 *   F_v       = k · max(0, n̂·w_local) · |w_local| · A · levelFraction
 *   F_luff    = k_luff · max(0, −n̂·w_local) · |w_local| · A · levelFraction
 *
 * plus gravity, Verlet damping, and a small seeded flutter. `|w_local|` is
 * exactly `windStrength` (the WIND_SPEED_SCALE constant is folded into `k`).
 * The local wind is smoothed toward its target with a first-order time constant
 * so a wind-direction change re-forms the sail over ~a second instead of
 * snapping.
 */

import * as THREE from 'three';
import {
  clothParams,
  createGrid,
  DEFAULT_BELLY_DEPTH,
  resetCloth,
  setWindTarget,
  stepCloth,
  type ClothParams,
  type ClothState,
} from './clothSolver';

export type ClothQuality = 'low' | 'high';

export interface SailClothOptions {
  columns?: number;
  rows?: number;
  quality?: ClothQuality;
  iterations?: number;
  damping?: number;
  stiffness?: number;
  windGain?: number;
  luffGain?: number;
  flutterGain?: number;
  flutterFrequency?: number;
  gravity?: number;
  windTau?: number;
  fixedDt?: number;
  /** Depth (metres) of the baked rest belly — the frozen/far sail's billow. */
  bellyDepth?: number;
  material?: THREE.Material;
  seed?: number;
}

interface ClothPreset {
  columns: number;
  rows: number;
  iterations: number;
  fixedDt: number;
}

const PRESETS: Record<ClothQuality, ClothPreset> = {
  // 8×10 segments = 99 particles, matching the old sail's vertex count.
  high: { columns: 8, rows: 10, iterations: 3, fixedDt: 1 / 60 },
  // 4×5 segments = 30 particles for mobile / mid-distance.
  low: { columns: 4, rows: 5, iterations: 2, fixedDt: 1 / 30 },
};

/** Distance LOD bands (metres) and hysteresis (metres). */
const LOD_NEAR = 25;
const LOD_FAR = 60;
const LOD_HYSTERESIS = 5;

function buildGeometry(state: ClothState): THREE.BufferGeometry {
  const { columns, rows, pos } = state;
  const stride = columns + 1;
  const count = state.count;
  const uvs = new Float32Array(count * 2);
  const indices: number[] = [];
  for (let r = 0; r <= rows; r += 1) {
    for (let c = 0; c <= columns; c += 1) {
      const i = r * stride + c;
      uvs[i * 2] = c / columns;
      uvs[i * 2 + 1] = 1 - r / rows;
    }
  }
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < columns; c += 1) {
      const a = r * stride + c;
      const b = a + 1;
      const e = a + stride;
      const f = e + 1;
      indices.push(a, e, b, b, e, f);
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(pos.slice(), 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex(indices);
  geometry.computeVertexNormals();
  return geometry;
}

export class SailCloth {
  readonly mesh: THREE.Mesh;

  private readonly seed: number;
  private readonly width: number;
  private readonly height: number;
  private readonly bellyDepth: number;

  private state: ClothState;
  private params: ClothParams;
  private geometry: THREE.BufferGeometry;
  private positionAttr: THREE.BufferAttribute;

  private baseQuality: ClothQuality;
  private gridQuality: ClothQuality;
  private effectiveQuality: ClothQuality | 'off';

  private worldDirection = 0;
  private worldStrength = 0;
  private level = 0;
  private furled = true;
  private lastDistance: number | undefined;
  private disposed = false;

  private readonly worldQuaternion = new THREE.Quaternion();
  private readonly tmpQuat = new THREE.Quaternion();
  private readonly tmpVec = new THREE.Vector3();

  /** Main-thread time of the last `update` (all fixed steps + normal write). */
  lastStepMs = 0;
  /** Number of fixed solver steps executed in the last `update`. */
  stepsLastFrame = 0;

  constructor(width: number, height: number, options: Partial<SailClothOptions> = {}) {
    this.width = width;
    this.height = height;
    this.seed = options.seed ?? 1337;
    this.bellyDepth = options.bellyDepth ?? DEFAULT_BELLY_DEPTH;
    this.baseQuality = options.quality ?? 'high';
    this.gridQuality = this.baseQuality;
    this.effectiveQuality = this.baseQuality;

    const preset = PRESETS[this.baseQuality];
    const columns = options.columns ?? preset.columns;
    const rows = options.rows ?? preset.rows;
    this.params = clothParams(options.iterations ?? preset.iterations, options.fixedDt ?? preset.fixedDt);
    this.params.damping = options.damping ?? this.params.damping;
    this.params.stiffness = options.stiffness ?? this.params.stiffness;
    this.params.windGain = options.windGain ?? this.params.windGain;
    this.params.luffGain = options.luffGain ?? this.params.luffGain;
    this.params.flutterGain = options.flutterGain ?? this.params.flutterGain;
    this.params.flutterFrequency = options.flutterFrequency ?? this.params.flutterFrequency;
    this.params.gravity = options.gravity ?? this.params.gravity;
    this.params.windTau = options.windTau ?? this.params.windTau;

    this.state = createGrid(
      columns,
      rows,
      width,
      height,
      this.seed,
      this.baseQuality === 'high',
      this.bellyDepth,
    );
    this.geometry = buildGeometry(this.state);
    this.mesh = new THREE.Mesh(
      this.geometry,
      options.material ?? new THREE.MeshStandardMaterial({ color: 0xf2ecdc, side: THREE.DoubleSide }),
    );
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    this.mesh.name = 'sail-cloth';
    this.positionAttr = this.geometry.getAttribute('position') as THREE.BufferAttribute;
    this.furled = false;
  }

  /** Authoritative wind direction (radians) and strength (0..1.5). */
  setWind(direction: number, strength: number): void {
    this.worldDirection = Number.isFinite(direction) ? direction : 0;
    this.worldStrength = Number.isFinite(strength) ? Math.min(1.5, Math.max(0, strength)) : 0;
  }

  /** Sail mesh world quaternion (converts world wind into sail-local space). */
  setWorldQuaternion(quaternion: THREE.Quaternion): void {
    this.worldQuaternion.copy(quaternion);
  }

  /** Sail level 0..4; 0 furls (reset + skip the solver). */
  setLevel(level: number): void {
    this.level = Number.isFinite(level) ? Math.min(4, Math.max(0, level)) : 0;
    this.state.levelFraction = this.level / 4;
    if (this.level === 0) {
      if (!this.furled) {
        resetCloth(this.state);
        this.writeGeometry();
        this.furled = true;
      }
    } else {
      this.furled = false;
    }
  }

  /** Set the base quality (mobile always caps at 'low' via its base quality). */
  setQuality(quality: ClothQuality): void {
    if (quality === this.baseQuality) return;
    this.baseQuality = quality;
    this.applyLod(this.lastDistance);
  }

  /**
   * Accumulate `dt` and run whole fixed steps. `distanceToCamera` (optional)
   * drives resolution/iteration LOD: <25 m high, 25–60 m low, >60 m frozen.
   */
  update(dt: number, distanceToCamera?: number): void {
    if (this.disposed) return;
    this.lastDistance = distanceToCamera;
    this.applyLod(distanceToCamera);
    if (!this.active()) {
      this.stepsLastFrame = 0;
      this.lastStepMs = 0;
      return;
    }

    this.updateWindTarget();

    const clamped = Number.isFinite(dt) ? Math.min(Math.max(0, dt), 1 / 15) : 0;
    this.state.acc += clamped;
    const started = performance.now();
    let steps = 0;
    while (this.state.acc >= this.params.fixedDt && steps < 4) {
      stepCloth(this.state, this.params);
      this.state.acc -= this.params.fixedDt;
      steps += 1;
    }
    this.lastStepMs = performance.now() - started;
    this.stepsLastFrame = steps;
    // Positions only change when a fixed step ran; skip the (relatively
    // expensive) normal recompute on idle accumulator frames.
    if (steps > 0) this.writeGeometry();
  }

  /** Return the cloth to its deterministic rest pose. */
  reset(): void {
    resetCloth(this.state);
    this.furled = this.level === 0;
    this.writeGeometry();
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.geometry.dispose();
  }

  /** Particle count of the currently-built grid. */
  get particleCount(): number {
    return this.state.count;
  }

  /** Constraint count (structural + bend) of the currently-built grid. */
  get constraintCount(): number {
    return (this.state.structural.length + this.state.bend.length) >> 1;
  }

  /** Currently active quality ('low' | 'high' | 'off'). */
  get quality(): ClothQuality | 'off' {
    return this.effectiveQuality;
  }

  /** Solver iterations for the currently-built grid. */
  get iterations(): number {
    return this.params.iterations;
  }

  private active(): boolean {
    return this.effectiveQuality !== 'off' && !this.furled;
  }

  private applyLod(distance: number | undefined): void {
    let effective: ClothQuality | 'off';
    if (this.baseQuality === 'low') {
      // Mobile profile is hard-capped at low — it never upgrades by distance.
      effective = 'low';
    } else if (distance === undefined || distance < 0) {
      effective = this.baseQuality;
    } else {
      effective = this.lodForDistance(distance);
    }
    const wantGrid: ClothQuality = effective === 'off' ? this.gridQuality : effective;
    if (wantGrid !== this.gridQuality) this.rebuild(wantGrid);
    this.effectiveQuality = effective;
  }

  private lodForDistance(distance: number): ClothQuality | 'off' {
    const current = this.effectiveQuality;
    if (current === 'high') {
      if (distance < LOD_NEAR + LOD_HYSTERESIS) return 'high';
      if (distance < LOD_FAR) return 'low';
      return 'off';
    }
    if (current === 'low') {
      if (distance < LOD_NEAR - LOD_HYSTERESIS) return 'high';
      if (distance < LOD_FAR + LOD_HYSTERESIS) return 'low';
      return 'off';
    }
    // current === 'off'
    if (distance < LOD_FAR - LOD_HYSTERESIS) return distance < LOD_NEAR ? 'high' : 'low';
    return 'off';
  }

  private rebuild(quality: ClothQuality): void {
    const preset = PRESETS[quality];
    const oldGeometry = this.geometry;
    this.state = createGrid(
      preset.columns,
      preset.rows,
      this.width,
      this.height,
      this.seed,
      quality === 'high',
      this.bellyDepth,
    );
    this.state.levelFraction = this.level / 4;
    this.params = { ...this.params, iterations: preset.iterations, fixedDt: preset.fixedDt };
    this.gridQuality = quality;
    this.geometry = buildGeometry(this.state);
    this.mesh.geometry = this.geometry;
    this.positionAttr = this.geometry.getAttribute('position') as THREE.BufferAttribute;
    oldGeometry.dispose();
  }

  /** Convert the authoritative world wind into the sail mesh's local frame. */
  private updateWindTarget(): void {
    if (this.worldStrength <= 1e-4) {
      setWindTarget(this.state, 0, 0, 0);
      return;
    }
    const dirX = Math.cos(this.worldDirection);
    const dirZ = -Math.sin(this.worldDirection);
    this.tmpVec.set(dirX, 0, dirZ).multiplyScalar(this.worldStrength);
    this.tmpQuat.copy(this.worldQuaternion).invert();
    this.tmpVec.applyQuaternion(this.tmpQuat);
    setWindTarget(this.state, this.tmpVec.x, this.tmpVec.y, this.tmpVec.z);
  }

  private writeGeometry(): void {
    (this.positionAttr.array as Float32Array).set(this.state.pos);
    this.positionAttr.needsUpdate = true;
    this.geometry.computeVertexNormals();
  }
}
